/**
 * Active-pill GEOMETRY helpers, shared by the web and native
 * {@link SegmentedControl}, plus the type of the optional externally-driven
 * progress input.
 *
 * Both platforms render the active pill the same way — one absolute overlay
 * measured to the active segment's box — so the box arithmetic belongs here
 * rather than being written out twice and drifting.
 */

/**
 * Structural mirror of a Reanimated `SharedValue<number>`.
 *
 * Declared here rather than imported, because `react-native-reanimated` is an
 * OPTIONAL peer of `@flashka/shared` (see `peerDependenciesMeta`) and
 * flashka-web does not install it at all — an `import type { SharedValue }`
 * would still have to resolve during the web typecheck and would break it. A
 * real `SharedValue<number>` is assignable to this.
 */
export interface SegmentedControlProgress {
  value: number;
}

/** A measured box in the track's inner-row coordinate space. */
export interface PillRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Is this box a real MEASUREMENT, or an `onLayout` that fired before the view
 * had a position?
 *
 * Native layout events round-trip through JS, and on a busy mount a view can
 * report a ZERO-SIZED box first — a first layout pass that beats the custom
 * font, a column measured while the screen is still being composed. That is not
 * a position, and treating it as one is worse than having none at all: the
 * consumers below latch "we have a box now", retire their measurement-free
 * fallback, and hand the indicator to an overlay that can only draw a
 * zero-sized (i.e. invisible) rect. Only the NATIVE files need this — web
 * measures synchronously in a layout effect, before paint.
 *
 * Deliberately NOT folded into {@link pillRectFor}: that helper is shared with
 * web, where `offsetWidth === 0` means a genuinely hidden control rather than
 * an unfinished measurement.
 */
export function isMeasuredRect(box: PillRect | undefined | null): box is PillRect {
  return !!box && box.width > 0 && box.height > 0;
}

/**
 * The pill box for one segment.
 *
 * An icon-only pill is a SQUARE (`width === height`) centred in its segment, so
 * a wide `fullWidth` column doesn't stretch the icon's pill into a rectangle
 * (it would stop reading as the DS icon-only cell). A no-op when the cell is
 * already square.
 *
 * Returns `null` for an unmeasured segment so callers can distinguish
 * "not measured yet" from "measured at the origin" — see {@link fillPillRects}.
 */
export function pillRectFor(
  box: PillRect | undefined | null,
  squarePill: boolean,
): PillRect | null {
  if (!box) return null;
  if (!squarePill) return { x: box.x, y: box.y, width: box.width, height: box.height };
  return {
    x: box.x + (box.width - box.height) / 2,
    y: box.y,
    width: box.height,
    height: box.height,
  };
}

/**
 * Should the active pill be squared to its own height rather than filling the
 * segment?
 *
 * ONLY when an icon-only control is NOT `fullWidth`. `iconOnly` in this DS means
 * a SQUARE cell (`w-10 px-0` at `l`), and squaring the pill there is a no-op —
 * the cell already is one. Under `fullWidth` the cells are 1/N of the track
 * instead, so the concept does not apply: squaring then shrinks the pill back to
 * a 40×40 chip floating in a much wider column, which is the "active tab hugs
 * the icon" the deck's Create row shipped with. Figma `67:20047` draws every
 * tab `flex-[1_0_0]` with the active one's fill spanning its whole share.
 */
export function shouldSquarePill(iconOnly: boolean, fullWidth: boolean): boolean {
  return iconOnly && !fullWidth;
}

/**
 * Replace every unmeasured slot with its nearest measured neighbour, so a pill
 * interpolating across the whole row HOLDS at the last known box instead of
 * flying to `(0, 0)`.
 *
 * This is not a corner case: segments report their boxes one `onLayout` at a
 * time, so on the first frames — and again whenever the segment set changes —
 * some indices are genuinely unmeasured while the pill is already on screen.
 *
 * Returns `null` when NOTHING has been measured yet (the caller should then
 * hide the pill rather than draw it somewhere arbitrary).
 */
export function fillPillRects(rects: readonly (PillRect | null)[]): PillRect[] | null {
  if (rects.length === 0) return null;
  const out = rects.slice();
  let carry: PillRect | null = null;
  for (let i = 0; i < out.length; i += 1) {
    if (out[i]) carry = out[i];
    else if (carry) out[i] = carry;
  }
  carry = null;
  for (let i = out.length - 1; i >= 0; i -= 1) {
    if (out[i]) carry = out[i];
    else if (carry) out[i] = carry;
  }
  return out.every((r): r is PillRect => r != null) ? (out as PillRect[]) : null;
}
